import { Plus, Trash2, Upload, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface LinksConfigSectionProps {
  links: any[];
  onChange: (links: any[]) => void;
}

export function LinksConfigSection({ links, onChange }: LinksConfigSectionProps) {
  return (
    <div className="space-y-3 border-t border-border/40 pt-4 text-left">
      <div className="flex justify-between items-center">
        <Label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">Resource Links</Label>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onChange([...links, { name_en: "", name_tr: "", url: "" }])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Link
        </Button>
      </div>
      <div className="space-y-2">
        {links.map((link, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <Input
              placeholder="Name (EN)"
              value={link.name_en}
              onChange={(e) => {
                const newLinks = [...links];
                newLinks[idx].name_en = e.target.value;
                onChange(newLinks);
              }}
            />
            <Input
              placeholder="Name (TR)"
              value={link.name_tr}
              onChange={(e) => {
                const newLinks = [...links];
                newLinks[idx].name_tr = e.target.value;
                onChange(newLinks);
              }}
            />
            <Input
              placeholder="URL"
              value={link.url}
              onChange={(e) => {
                const newLinks = [...links];
                newLinks[idx].url = e.target.value;
                onChange(newLinks);
              }}
            />
            <Button
              size="icon"
              variant="ghost"
              className="text-destructive h-9 w-9"
              onClick={() => onChange(links.filter((_, i) => i !== idx))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

interface MetricRowProps {
  m: any;
  idx: number;
  metrics: any[];
  onChange: (metrics: any[]) => void;
}

function MetricRow({ m, idx, metrics, onChange }: MetricRowProps) {
  const updateMetric = (updates: any) => {
    const newMetrics = [...metrics];
    newMetrics[idx] = { ...newMetrics[idx], ...updates };
    onChange(newMetrics);
  };

  return (
    <div className="grid grid-cols-6 gap-2 items-center p-2 border border-border/30 rounded-xl bg-muted/10">
      <Input
        className="col-span-1"
        placeholder="Val (EN)"
        value={m.value_en}
        onChange={(e) => updateMetric({ value_en: e.target.value })}
      />
      <Input
        className="col-span-1"
        placeholder="Val (TR)"
        value={m.value_tr}
        onChange={(e) => updateMetric({ value_tr: e.target.value })}
      />
      <Input
        className="col-span-1.5"
        placeholder="Label (EN)"
        value={m.label_en}
        onChange={(e) => updateMetric({ label_en: e.target.value })}
      />
      <Input
        className="col-span-1.5"
        placeholder="Label (TR)"
        value={m.label_tr}
        onChange={(e) => updateMetric({ label_tr: e.target.value })}
      />
      <select
        className="col-span-1 border border-border rounded p-1 text-xs bg-background text-foreground"
        value={m.color}
        onChange={(e) => updateMetric({ color: e.target.value })}
      >
        <option value="text-brand-accent">Green Accent</option>
        <option value="text-brand-primary">Blue Primary</option>
        <option value="text-brand-secondary">Cobalt Green</option>
        <option value="text-red-500">Red</option>
        <option value="text-yellow-500">Yellow</option>
      </select>
      <div className="col-span-1 flex items-center gap-1.5">
        <select
          className="border border-border rounded p-1 text-xs flex-1 bg-background text-foreground"
          value={m.font_size}
          onChange={(e) => updateMetric({ font_size: e.target.value })}
        >
          <option value="text-lg">Small</option>
          <option value="text-xl">Medium</option>
          <option value="text-2xl">Large</option>
          <option value="text-3xl">Extra Large</option>
        </select>
        <Button
          size="icon"
          variant="ghost"
          className="text-destructive h-8 w-8 shrink-0"
          onClick={() => onChange(metrics.filter((_, i) => i !== idx))}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface MetricsConfigSectionProps {
  metrics: any[];
  onChange: (metrics: any[]) => void;
}

export function MetricsConfigSection({ metrics, onChange }: MetricsConfigSectionProps) {
  const addMetric = () => {
    onChange([
      ...metrics,
      {
        value_en: "",
        value_tr: "",
        label_en: "",
        label_tr: "",
        color: "text-brand-accent",
        font_size: "text-2xl"
      }
    ]);
  };

  return (
    <div className="space-y-3 border-t border-border/40 pt-4 text-left">
      <div className="flex justify-between items-center">
        <Label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">Metrics Cards</Label>
        <Button size="sm" variant="outline" onClick={addMetric}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Metric
        </Button>
      </div>
      <div className="space-y-2">
        {metrics.map((m, idx) => (
          <MetricRow key={idx} m={m} idx={idx} metrics={metrics} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

interface GalleryConfigSectionProps {
  images: string[];
  onChange: (images: string[]) => void;
  uploading: boolean;
  onUploadClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function GalleryConfigSection({
  images,
  onChange,
  uploading,
  onUploadClick,
  fileInputRef,
  handleImageUpload
}: GalleryConfigSectionProps) {
  return (
    <div className="space-y-3 border-t border-border/40 pt-4 text-left">
      <div className="flex justify-between items-center">
        <Label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">Screenshots Gallery</Label>
        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
        <Button size="sm" variant="outline" disabled={uploading} onClick={onUploadClick}>
          <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? "Uploading..." : "Upload Screenshot"}
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-4 mt-2">
        {images.map((imgUrl, idx) => (
          <div
            key={idx}
            className="relative aspect-video rounded-xl overflow-hidden border border-border/40 group bg-muted/15 flex items-center justify-center p-1"
          >
            <img src={imgUrl} alt="Thumbnail" className="max-h-full max-w-full object-contain" />
            <button
              onClick={() => onChange(images.filter((_, i) => i !== idx))}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center border border-white/20 transition-all opacity-0 group-hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
